import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';

import CursorListBody from '../shared/cursor_list_body';
import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import { FilterBar, Segmented } from '../shared/controls';
import { Ledger, StatusCell, What, Who } from '../shared/cells';
import ListCard, { shown_text } from '../payouts/list_card';
import useCursorList from '../shared/use_cursor_list';
import { replace_search } from '../shared/url_query';
import { fetch_invoice_pdf, fetch_invoices_list, fetch_l1 } from '../api';
import {
  VIEWS,
  VIEW_AWAITING,
  awaiting_view,
  invoice_error,
  invoice_pdf_name,
  list_view
} from './adapters';
import { EM_DASH } from '../format';
import { awaiting_l1_params } from './requests';
import { invoices_search, invoices_view_from_search } from './url_filters';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { collect_all_pages } from '../../../util_components/csv_export/collect_pages';
import { auth_only } from '../payouts/csv';
import {
  AWAITING_COLUMNS as AWAITING_CSV_COLUMNS,
  INVOICE_COLUMNS as INVOICE_CSV_COLUMNS,
  invoices_csv_name,
  skip_empty_pages
} from './csv';
import '../payouts/payouts.css';

/*
 * the Invoices tab (/teacher-payments/invoices): the month's confirmed and replaced invoices with
 * their PDF, and the closed teacher-months still awaiting the teacher's invoice.
 */
const LIST_FAILED_TEXT = 'Could not load the invoices.';
const AWAITING_FAILED_TEXT = 'Could not load the teachers awaiting an invoice.';
const PDF_FAILED_TEXT = 'Could not download the invoice PDF. Try again.';

// Four tracks (list-row pattern): teacher · invoice · money ledger · status and PDF; cards below 660px.
const invoice_columns = (on_pdf, busy_uuid) => {
  const pdf_button = (r, className) => {
    const busy = busy_uuid === r.invoice_uuid;
    return (
      <button
        type="button"
        className={className}
        aria-label={`Download invoice ${r.invoice_number} PDF`}
        onClick={busy ? undefined : () => on_pdf(r)}
        aria-disabled={busy ? 'true' : undefined}
      >
        {busy ? 'Downloading…' : 'Download PDF'}
      </button>
    );
  };
  const chip = (r) => <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>;
  const meta = (r) => [r.superseded_at ? `Replaced ${r.superseded_at}` : null];
  return [
    {
      key: 'teacher_name',
      role: 'identifier',
      label: 'Teacher',
      track: { compact: '24%', full: '24%' },
      render: (r) => <Who name={r.teacher_name} />
    },
    {
      key: 'invoice_number',
      label: 'Invoice',
      render: (r) => (
        <What
          title={<span className="tp-mono tp-inv-no">{r.invoice_number}</span>}
          lines={[
            `Close version ${r.version}`,
            r.confirmed_at && r.confirmed_at !== '—' ? `Confirmed ${r.confirmed_at}` : null
          ]}
        />
      )
    },
    {
      key: 'payable',
      role: 'amount',
      label: 'Money',
      align: 'right',
      track: { compact: '196px', full: '216px' },
      render: (r) => (
        <Ledger
          items={[
            { label: 'Taxable', value: r.taxable },
            { label: r.gst_label, value: r.gst_amount },
            { label: 'Invoice total', value: r.invoice_total },
            {
              label: r.tds_label,
              value: r.tds_amount,
              tone: /^[-−]/.test(r.tds_amount) ? 'neg' : ''
            }
          ]}
          total={{ label: 'We pay', value: r.payable }}
        />
      )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '132px', full: '160px' },
      render: (r) => (
        <StatusCell
          chip={chip(r)}
          meta={meta(r)}
          actions={<span className="tp-c-acts tp-actions">{pdf_button(r, 'tp-link')}</span>}
        />
      ),
      card_render: (r) => <StatusCell chip={chip(r)} meta={meta(r)} />
    },
    {
      key: 'pdf',
      role: 'actions',
      label: 'PDF',
      in_table: false,
      card_render: (r) => pdf_button(r, 'tp-btn-out')
    }
  ];
};

// "2 reminders sent" / "Last reminder <when>"; an unknown count (failed read) says so.
const reminders_title = (r) => {
  if (r.reminders_sent === EM_DASH) return 'Reminders not known';
  return `${r.reminders_sent} ${r.reminders_sent === '1' ? 'reminder' : 'reminders'} sent`;
};
const last_reminder_line = (r) => {
  if (r.last_reminder === EM_DASH) return null;
  if (r.last_reminder === 'None yet') return 'No reminder yet';
  return `Last reminder ${r.last_reminder}`;
};

const awaiting_columns = [
  {
    key: 'teacher',
    role: 'identifier',
    label: 'Teacher',
    track: { compact: '30%', full: '28%' },
    render: (r) => <Who name={r.teacher_name} meta={[r.teacher_email]} />
  },
  {
    key: 'reminders_sent',
    label: 'Reminders',
    render: (r) => <What title={reminders_title(r)} lines={[last_reminder_line(r)]} />
  },
  {
    key: 'payable',
    role: 'amount',
    label: 'Payable',
    align: 'right',
    track: { compact: '140px', full: '160px' },
    render: (r) => <span className="tp-c-amt">{r.payable}</span>,
    card_render: (r) => <Ledger items={[{ label: 'Payable', value: r.payable }]} />
  },
  {
    key: 'note',
    role: 'status',
    label: 'Status',
    track: { compact: '168px', full: '200px' },
    render: (r) =>
      r.reopened ? (
        <StatusCell
          chip={<StatusPill tone="orange">Reopened</StatusPill>}
          meta={['Closed again: the teacher confirms the new invoice.']}
        />
      ) : (
        <StatusCell chip={<StatusPill tone="gray">Not confirmed yet</StatusPill>} />
      )
  }
];

const InvoicesTab = ({ pay_month, month_label, on_error, on_notify }) => {
  const history = useHistory();
  const location = useLocation();
  const [view, setView] = useState(() => invoices_view_from_search(location.search));
  const [busy_uuid, setBusyUuid] = useState('');
  const awaiting = view === VIEW_AWAITING;

  // The list mirrors itself into ?view= (replace: a filter is not a history step).
  useEffect(() => {
    replace_search(history, location.pathname, (search) => invoices_search(search, view));
  }, [view, history, location.pathname, location.search]);

  const read = useCallback(
    (cursor) =>
      awaiting
        ? fetch_l1(awaiting_l1_params(pay_month, cursor || 0)).then((d) => {
            const v = awaiting_view(d, cursor || 0);
            return { rows: v.rows, next: v.next_offset };
          })
        : fetch_invoices_list(pay_month, view, cursor).then((d) => {
            const v = list_view(d);
            return { rows: v.rows, next: v.next_after_id };
          }),
    [pay_month, view, awaiting]
  );
  const list = useCursorList(
    read,
    awaiting ? AWAITING_FAILED_TEXT : LIST_FAILED_TEXT,
    on_error,
    invoice_error
  );

  const download_pdf = (row) => {
    setBusyUuid(row.invoice_uuid);
    fetch_invoice_pdf(row.invoice_uuid)
      .then(({ blob, file_name }) => {
        setBusyUuid('');
        saveAs(blob, invoice_pdf_name(row.invoice_number, file_name));
      })
      .catch((e) => {
        setBusyUuid('');
        const v = invoice_error(e, PDF_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, PDF_FAILED_TEXT);
        else on_notify('error', v.text);
      });
  };

  return (
    <div className="tp-inv-tab">
      <p className="tp-po-lead">
        Teachers confirm their invoice in the teacher app after the month is closed. A month that is
        reopened and closed again needs a new confirmation; the earlier invoice shows as replaced.
      </p>

      <FilterBar label="Invoices filters">
        <Segmented label="Invoices list" options={VIEWS} value={view} onChange={setView} />
      </FilterBar>

      <ListCard
        title={`${awaiting ? 'Awaiting invoice' : 'Invoices'} · ${month_label}`}
        sub={shown_text(list)}
        acts={
          <DownloadCsvButton
            key={view}
            columns={awaiting ? AWAITING_CSV_COLUMNS : INVOICE_CSV_COLUMNS}
            load_rows={() => collect_all_pages(awaiting ? skip_empty_pages(read) : read)}
            file_name={invoices_csv_name(
              (VIEWS.find((v) => v.value === view) || {}).label || 'Invoices',
              pay_month
            )}
            on_error={auth_only(invoice_error, on_error)}
          />
        }
      >
        {awaiting ? (
          <CursorListBody
            list={list}
            render_rows={(rows) => (
              <DataTable
                columns={awaiting_columns}
                rows={rows}
                rowKey={(r) => r.teacher_uuid}
                emptyText="No closed teacher-month is waiting for an invoice."
                card_below={660}
              />
            )}
          />
        ) : (
          <CursorListBody
            list={list}
            render_rows={(rows) => (
              <DataTable
                columns={invoice_columns(download_pdf, busy_uuid)}
                rows={rows}
                rowKey={(r) => r.invoice_uuid}
                emptyText="No invoices for this month and filter."
                card_below={660}
              />
            )}
          />
        )}
      </ListCard>
    </div>
  );
};

export default InvoicesTab;
